import type { BlockReason, CreateBlockReason, UpdateBlockReason } from "@dentacore/shared-types";

import { api } from "./client";

/**
 * Why a chair is held when nobody is in it — the active branch's own list
 * (STORY-046c, decisions G12 and G25).
 *
 * **Branch-scoped**, like the procedure catalogue beside it, so the branch comes
 * from the header the guard checks rather than from a parameter.
 *
 * **Readable by everybody, writable by an admin.** The booking form needs the
 * list to offer it, and anyone at the desk holds a chair; changing the
 * vocabulary is administration.
 */
export function listBlockReasons(
  includeRetired = false,
  signal?: AbortSignal,
): Promise<BlockReason[]> {
  const query = includeRetired ? "?includeRetired=true" : "";
  return api<BlockReason[]>(`/block-reasons${query}`, { signal, branchScoped: true });
}

export function createBlockReason(body: CreateBlockReason): Promise<BlockReason> {
  return api<BlockReason>("/block-reasons", { method: "POST", body, branchScoped: true });
}

/** Rename, retire (`isActive: false`), bring back, or move it in the list. */
export function updateBlockReason(id: string, body: UpdateBlockReason): Promise<BlockReason> {
  return api<BlockReason>(`/block-reasons/${id}`, { method: "PATCH", body, branchScoped: true });
}

/**
 * Remove one outright — **only while nothing has used it.**
 *
 * Unlike a procedure code (C3), an unused reason really is deletable: it points
 * at nothing, and making a practice keep a typo for ever is not a rule. One
 * that *is* in use comes back as a 409 naming the count and saying to retire.
 */
export function removeBlockReason(id: string): Promise<void> {
  return api<void>(`/block-reasons/${id}`, { method: "DELETE", branchScoped: true });
}

/**
 * Accept the offered starter set — TP10's arrangement, applied by G25.
 *
 * **Content offered, then owned.** The rows this creates are ordinary rows
 * inside this branch, editable from the moment they exist, and nothing reads
 * the offered list again — which is the whole of why an offered list is
 * compatible with *data is configured, not coded*.
 */
export function acceptStarterBlockReasons(): Promise<BlockReason[]> {
  return api<BlockReason[]>("/block-reasons/starter-set", { method: "POST", branchScoped: true });
}
